<script setup>
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import groupBy from 'lodash/groupBy'

import {ref, unref, computed, nextTick, inject, onBeforeUnmount, onMounted} from 'vue';
import {storeToRefs} from 'pinia';
import {useDebug} from '../../composables/useDebug';
import axios from '../../setup/axios';
import {useSearchStore} from './store/useSearchStore';

import SearchForm from './components/SearchForm.vue';
import QuickSearchResults from './components/QuickSearchResults.vue';
import {useSearchMixins} from "../../composables/useSearchMixins";

const {log, error} = useDebug();
const $bus = inject('$bus');

const store = useSearchStore();

const API_ENDPOINT = '/o/merkur-2021-search/tgb/search';

const props = defineProps({
    dataSearchUrl: {
        type: String,
        default: "./search",
    }
});

const suggestions = ref(null);
const results = ref([]);
const doneSearch = ref(false);
const noSearch = ref(false);
const pageResults = ref([]);
const totalResults = ref(0);
const startingIndex = ref(0);
const currentPage = ref(null);
const busy = ref(true);
const errors = ref([]);
const facets = ref([]);
const userLang = ref(null);

const quickSearchContainerRef = ref(null);
const categoryResultsRef = ref(null);
const categoryRef = ref(null);

const abortController = ref(null);

const quickSearchResultsRef = ref(null);

const {defaultSearch} = useSearchMixins();

const range = computed(() => {
    if (currentPage === null) {
        return {
            from: 1,
            to: defaultSearch.options.resultsPerPage,
        };
    }

    const calculatedEnd = currentPage * defaultSearch.options.resultsPerPage;
    const calculatedStart = calculatedEnd - defaultSearch.options.resultsPerPage;

    return {
        from: calculatedStart < 1 ? 1 : calculatedStart,
        to: calculatedEnd > totalResults ? totalResults : calculatedEnd,
    };
});


const closeQuickSearch = () => {
    if (abortController.value) {
        abortController.value.abort();
    }

    store.searchQuery = null;

    resetRequest();

    if (document.body.classList.contains('active-quick-search')) {
        document.body.classList.remove('active-quick-search');
    }
};

const handleResult = (resultData) => {

    const result = unref(resultData);

    if (result.request === undefined || result.request === null) {
        results.value = [];
        results.value = groupBy(result.results, 'type');

        totalResults.value = result.results.length;

    } else {
        let groupedResults = groupBy(result.results, 'type');

        if (result.request.currentPage === 1) {
            results.value = groupedResults;
        } else {
            results.value = results.value.concat(groupedResults);
        }
        if (noSearch.value) {
            totalResults.value = results.value[Object.keys(results.value)[0]].length;
        } else {
            totalResults.value = result.hasOwnProperty('totalCount') ? result.totalCount : results.value.length;
        }

        if (result.hasOwnProperty('pageResults')) {
            pageResults.value = result.pageResults;
        }

        startingIndex.value = result.hasOwnProperty('startIndex') ? result.startIndex : 0;
        currentPage.value = Math.ceil(startingIndex.value / defaultSearch.options.resultsPerPage) + 1;

        extractFacets(result.facets);
    }

    document.body.classList.remove('has-scrolllock');
    focusFirstChild();

};

const focusFirstChild = () => {
    nextTick(() => {
        if(quickSearchResultsRef) {
            let parent = quickSearchResultsRef.value;

            if (parent) {
                if (parent.querySelector('.search-result') !== undefined) {
                    let firstResult = parent.querySelector('.search-result');
                    if (firstResult) {
                        firstResult.focus();
                    }
                }
            }
        }
    });
}

const extractFacets = (rawFacets) => {
    facets.value = [];

    const others = filter(rawFacets, o => o.type !== 'vocabularies');
    const others_array = [...others].slice().sort(function (a, b) {
        return a.id - b.id;
    });
    forEach(others_array, o => {
        if (o.facets && o.facets.length && o.id !== null) {
            facets.value.push(o);
        }
    });
};
const handleSearch = (query = null, page = 1) => {
    store.customResultIndex = 0;
    store.quickResults = [];

    store.searchQuery = query || store.searchQuery;
    page = page > 1 ? page : 1;
    results.value = [];
    totalResults.value = 0;

    busy.value = true;

    if (abortController.value) {
        abortController.value.abort();
    }

    abortController.value = new AbortController();

    if ((store.searchQuery === undefined || store.searchQuery === null || store.searchQuery.length === 0) && suggestions.value !== null) {
        if (suggestions.value.results !== undefined && suggestions.value.results.length > 0) {
            noSearch.value = true;

            handleResult(suggestions.value);
            resetRequest();
        }

    } else if (store.searchQuery !== null && store.searchQuery.length >= defaultSearch.options.minimumSearchQueryLength) {
        const query = store.searchQuery.trim();
        noSearch.value = false;

        const data = {
            keywords: escape(query),
            currentPage: page,
            pageSize: defaultSearch.options.resultsPerPage,
            locale: window.Liferay.ThemeDisplay.getLanguageId(),
            searchGroupIds: [window.Liferay.ThemeDisplay.getScopeGroupIdOrLiveGroupId()],
            scopeGroupId: window.Liferay.ThemeDisplay.getScopeGroupIdOrLiveGroupId(),
        };

        axios.post(API_ENDPOINT, data, {responseType: 'json', signal: abortController.value.signal})
            .then(response => {
                log?.('🕵️‍ Search response:', response);

                handleResult(response.data);

            })
            .catch(reason => {
                error(reason);
                errors.value.push(reason);
            })
            .finally(() => {
                resetRequest();

                nextTick(() => {
                    doneSearch.value = true;
                });
            });

    } else {
        resetResults();
        resetRequest();
        if (page === 1) {
            focusFirstChild();
        }
    }

}

const resetRequest = () => {
    abortController.value = null;
    busy.value = false;
};

const escape = (string) => {
    return string.replace(/"/g, '\\"').replace(/'/g, "\\'");
};

const resetResults = () => {
    totalResults.value = 0;
    store.searchQuery = null;
    window.history.replaceState(null, null, window.location.pathname);
};

const handleKeyboard = (event) => {
    if (event.key === 'Escape') {
        $bus.emit('restartSearch');
    }
};


const resetSearch = () => {
    doneSearch.value = false;
    results.value = [];
    totalResults.value = 0;
}


const restartSearch = () => {
    doneSearch.value = false;
    store.searchQuery = null;
    results.value = null;
    totalResults.value = 0;
    handleSearch();

};

const startSearch = () => {
    handleSearch(store.searchQuery);
};

$bus.on('restartSearch', restartSearch);
$bus.on('resetSearch', resetSearch);
$bus.on('startSearch', startSearch);


onMounted(() => {
    document.addEventListener("keyup", handleKeyboard, true);
    document.addEventListener("keydown", (e) => {
        if (e.keyCode===27 && document.body.classList.contains('active-quick-search')) {
            document.body.classList.remove('active-quick-search');
        }
    });

    if (window.searchSuggestions &&
        window.searchSuggestions.results &&
        window.searchSuggestions.results.length > 0) {

        suggestions.value = window.searchSuggestions;
        suggestions.value.request = {
            "currentPage": 1,
        };
    } else {
        suggestions.value = null;
    }

    const element = document.querySelector('body');
    let prevState = element.classList.contains('active-quick-search');

    const observer = new MutationObserver((mutations) => {

        mutations.forEach((mutation) => {
            const {target} = mutation;
            if (mutation.attributeName === 'class') {
                const currentState = mutation.target.classList.contains('active-quick-search');
                if (prevState !== currentState) {
                    prevState = currentState;
                    if (currentState) {
                        handleSearch();
                    }
                }
            }
        });
    });
    observer.observe(element, {
        attributes: true,
        attributeOldValue: true,
        attributeFilter: ['class']
    });

    userLang.value = window.Liferay !== undefined ? window.Liferay.ThemeDisplay.getLanguageId() : '';
});
onBeforeUnmount(() => {
    document.removeEventListener("keyup", handleKeyboard, false);// Fails
});

const {searchQuery} = storeToRefs(store);


</script>

<template>

    <div class="quick-search" :class="{ 'is-searching': busy }">
        <div class="close-quick-search icon" @click="closeQuickSearch">
            <svg width="37" height="37" viewBox="0 0 37 37">
                <path
                    d="M21.4501 18.2677C21.3034 18.1214 21.3031 17.884 21.4494 17.7374C21.4496 17.7371 21.4498 17.7369 21.4501 17.7367L35.3446 3.84368C36.2231 2.96389 36.2221 1.53848 35.3423 0.65993C34.4625 -0.218617 33.0371 -0.217609 32.1586 0.66218L18.2656 14.5492C18.1193 14.6958 17.8819 14.6961 17.7352 14.5499C17.735 14.5496 17.7348 14.5494 17.7346 14.5492L3.84157 0.66218C2.96302 -0.216781 1.53827 -0.217117 0.659319 0.66143C-0.219642 1.53998 -0.219978 2.96473 0.658569 3.84368L14.5501 17.7367C14.6967 17.8829 14.697 18.1204 14.5507 18.267C14.5505 18.2672 14.5503 18.2675 14.5501 18.2677L0.658569 32.1622H0.658569C-0.219979 33.0411 -0.219643 34.4659 0.659319 35.3444C1.53828 36.223 2.96302 36.2226 3.84157 35.3437L17.7346 21.4492C17.8808 21.3025 18.1183 21.3022 18.2649 21.4485C18.2651 21.4487 18.2653 21.449 18.2656 21.4492L32.1586 35.3437C33.0371 36.2226 34.4619 36.223 35.3408 35.3444C36.2198 34.4659 36.2201 33.0411 35.3416 32.1622L21.4501 18.2677Z"
                    fill="white" fill-opacity="0.5"/>
            </svg>
        </div>
        <div class=" quick-search-container" ref="quickSearchContainerRef">
            <SearchForm ref="form" v-model="searchQuery" :busy="busy" type="quicksearch"
                        @reset="resetResults($event)" @search="handleSearch"/>
            <div class="search-results" v-show="totalResults > 0" ref="quickSearchResultsRef">
                <div v-for="(category,index,key) in results" class="category" :id="`category-${key}`"
                     ref="categoryResultsRef"
                     :data-index="key">

                    <template v-if="(category && key < 2)">

                        <div v-if="!noSearch" class="title category-title">
                            <span v-if="index.includes('com.liferay') || index.includes('dccs.liferay')" v-html="$t('search.' + index.replaceAll('.','_'))"></span>
                            <span v-else>{{ index }} </span>
                        </div>
                        <div v-else class="title category-title">
                            <span v-if="index.includes('com.liferay') || index.includes('dccs.liferay')" v-html="$t('search.' + index.replaceAll('.','_'))"></span>
                            <span v-else>{{ index }} </span>
                        </div>

                        <QuickSearchResults
                            v-if="(category && key < 3 && !noSearch && totalResults > 0)"
                            :results="category"
                            :page-results="pageResults"
                            :search-query="searchQuery"
                            :current-page="currentPage"
                            :total-results="totalResults"
                            :noSearch="noSearch"
                            :busy="busy"
                            :search-suggestions="false"
                            ref="categoryRef"
                            :key="`searchable-result-${key}`"
                        />

                        <QuickSearchResults
                            v-else-if="(category && noSearch && totalResults > 0)"
                            :results="category"
                            :page-results="pageResults"
                            :search-query="searchQuery"
                            :current-page="currentPage"
                            :total-results="totalResults"
                            :noSearch="noSearch"
                            :busy="busy"
                            :search-suggestions="true"
                            :search-url="dataSearchUrl"
                            ref="categoryRef"
                            :key="`not-searchable-result-${key}`"

                        />
                    </template>

                </div>
                <a class="more-results" :href="dataSearchUrl + store.searchQuery"
                   v-if="doneSearch && totalResults > 2 && store.searchQuery && store.searchQuery.length > 0 && (!noSearch)">

                    <span v-if="userLang !== null && userLang === 'sl_SI'" v-html="$t('search.moreResultsFirst') + ' ' + totalResults + ' ' +  $t('search.moreResultsSecond')"/>

                    <span v-else v-html="totalResults  + ' '  + $t('search.moreResults')"></span>

                    <svg class="icon icon-right" width="16px" height="17px">
                        <use xlink:href="#search-icon-right-more"></use>
                    </svg>
                </a>
            </div>

            <a class="more-results"
               :href="dataSearchUrl + store.searchQuery"
               v-if="doneSearch && totalResults > 2 && store.searchQuery && store.searchQuery.length > 0 && noSearch && !busy">
                <span v-html="totalResults  + ' ' + $t('search.moreResults')"></span>

                <svg class="icon icon-right" width="16px" height="17px">
                    <use xlink:href="#search-icon-right-more"></use>
                </svg>
            </a>
            <div class="empty search-empty"
                 v-if="doneSearch && !busy && (totalResults <= 0) && store.searchQuery && store.searchQuery.length >= defaultSearch.options.minimumSearchQueryLength && !noSearch">
                <h1 class="empty-heading text-title title is-1" v-html="$t('search.oops')"></h1>
                <p class="empty-text has-regular-text" v-html="$t('search.noResults')"></p>
            </div>
        </div>
    </div>
</template>
